import { cleanBracketedFieldRef } from './fieldIdentity'

export type TableauDateTimeGrain =
  | 'year'
  | 'quarter'
  | 'month'
  | 'week'
  | 'day'
  | 'hour'
  | 'minute'
  | 'second'

export type TableauDateCalendar = 'standard_gregorian' | 'iso_8601_week'

const DATE_GRAIN_SET = new Set<TableauDateTimeGrain>([
  'year',
  'quarter',
  'month',
  'week',
  'day',
  'hour',
  'minute',
  'second',
])

const WRAPPED_GRAIN_RE = /^(year|quarter|month|week|day|hour|minute|second)\s*\(\s*(.+?)\s*\)\s*$/i

const escapeRegex = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export const isTableauDateLikeDataType = (raw: unknown): boolean => {
  const s = String(raw ?? '').trim().toLowerCase()
  return s.includes('date') || s.includes('time')
}

export const normalizeTableauDateTimeGrain = (raw: unknown): TableauDateTimeGrain | null => {
  const s = String(raw ?? '').trim().toLowerCase()
  return DATE_GRAIN_SET.has(s as TableauDateTimeGrain) ? (s as TableauDateTimeGrain) : null
}

export const normalizeTableauDateCalendar = (raw: unknown): TableauDateCalendar => {
  const s = String(raw ?? '').trim().toLowerCase()
  return s.includes('iso') ? 'iso_8601_week' : 'standard_gregorian'
}

const unwrapWrappedFieldName = (raw: unknown): { baseField: string; grain: TableauDateTimeGrain | null } => {
  const value = String(raw ?? '').trim()
  if (!value) return { baseField: '', grain: null }
  const match = value.match(WRAPPED_GRAIN_RE)
  if (!match) return { baseField: value, grain: null }
  return {
    baseField: String(match[2] ?? '').trim(),
    grain: normalizeTableauDateTimeGrain(match[1]),
  }
}

export const extractTableauDatePillBaseFieldName = (pill: any): string => {
  const candidates = [
    pill?.field_name,
    pill?.field,
    pill?.display_name,
    pill?.label,
    pill?.alias,
    pill?.name,
  ]
  for (const candidate of candidates) {
    const raw = String(candidate ?? '').trim()
    if (!raw) continue
    const unwrapped = unwrapWrappedFieldName(raw)
    const cleaned = cleanBracketedFieldRef(unwrapped.baseField) || unwrapped.baseField
    if (cleaned) return cleaned
  }
  return ''
}

export const resolveTableauDatePillGrain = (pill: any): TableauDateTimeGrain | null => {
  const explicit = normalizeTableauDateTimeGrain(pill?.time_grain ?? pill?.timeGrain)
  if (explicit) return explicit
  const inferred = unwrapWrappedFieldName(
    pill?.display_name ?? pill?.label ?? pill?.alias ?? pill?.field_name ?? pill?.field ?? '',
  )
  return inferred.grain
}

export const resolveTableauDatePillDiscrete = (pill: any): boolean => {
  if (typeof pill?.is_discrete === 'boolean') return pill.is_discrete
  const typeRaw = String(pill?.type ?? '').trim().toLowerCase()
  if (typeRaw === 'discrete') return true
  if (typeRaw === 'continuous') return false
  return true
}

export const isTableauDatePill = (pill: any): boolean =>
  Boolean(resolveTableauDatePillGrain(pill) || isTableauDateLikeDataType(pill?.data_type))

export const buildTableauDatePillLabel = (pill: any): string => {
  const baseField = extractTableauDatePillBaseFieldName(pill)
  if (!baseField) return ''
  const grain = resolveTableauDatePillGrain(pill)
  if (!grain) return baseField
  return `${grain.toUpperCase()}(${baseField})`
}

export const quoteTableauFieldReference = (fieldName: string): string => {
  const cleaned = cleanBracketedFieldRef(String(fieldName ?? '').trim()) || String(fieldName ?? '').trim()
  return `[${cleaned.replace(/\]/g, ']]')}]`
}

export const buildTableauDatePillFormula = (pill: any): string => {
  const baseField = extractTableauDatePillBaseFieldName(pill)
  if (!baseField) return ''
  const fieldRef = quoteTableauFieldReference(baseField)
  const grain = resolveTableauDatePillGrain(pill)
  if (!grain) return fieldRef
  const isDiscrete = resolveTableauDatePillDiscrete(pill)
  if (isDiscrete && grain === 'year') return `YEAR(${fieldRef})`
  return `DATETRUNC('${grain}', ${fieldRef})`
}

export type ParsedTableauDatePillFormula =
  | {
      ok: true
      time_grain: TableauDateTimeGrain | null
      inferred_is_discrete: boolean | null
    }
  | {
      ok: false
      error: string
    }

export const parseTableauDatePillFormula = (
  formula: string,
  baseFieldName: string,
): ParsedTableauDatePillFormula => {
  const raw = String(formula ?? '').trim()
  if (!raw) return { ok: false, error: 'Formula is required.' }

  const baseField = extractTableauDatePillBaseFieldName({ field_name: baseFieldName })
  if (!baseField) return { ok: false, error: 'Date pill is missing its base field.' }

  const fieldRef = quoteTableauFieldReference(baseField)
  const escapedFieldRef = escapeRegex(fieldRef)
  if (raw === fieldRef || new RegExp(`^DATE\\(\\s*${escapedFieldRef}\\s*\\)$`, 'i').test(raw)) {
    return { ok: true, time_grain: null, inferred_is_discrete: null }
  }

  const grainPattern = '(year|quarter|month|week|day|hour|minute|second)'
  const truncMatch = raw.match(
    new RegExp(`^DATETRUNC\\(\\s*['"]${grainPattern}['"]\\s*,\\s*${escapedFieldRef}\\s*\\)$`, 'i'),
  )
  if (truncMatch) {
    return {
      ok: true,
      time_grain: normalizeTableauDateTimeGrain(truncMatch[1]),
      inferred_is_discrete: null,
    }
  }

  const partMatch = raw.match(new RegExp(`^${grainPattern}\\(\\s*${escapedFieldRef}\\s*\\)$`, 'i'))
  if (partMatch) {
    return {
      ok: true,
      time_grain: normalizeTableauDateTimeGrain(partMatch[1]),
      inferred_is_discrete: true,
    }
  }

  return {
    ok: false,
    error: `Use ${fieldRef}, DATETRUNC('<part>', ${fieldRef}), or a simple date function like MONTH(${fieldRef}).`,
  }
}
